import { useParams } from 'react-router-dom';
import { Padding } from 'Components/Padding';
import Breadcrumb from 'Components/Breadcrumb/Breadcrumb';
import CertificateDownload from 'Components/CertificateDownload/CertificateDownload';
import CourseStatusChip from 'Components/CourseStatusChip';
import GenericTableText from 'Components/GenericTableText/GenericTableText';
import SideBarContainer from 'Containers/SideBarContainer';
import { useHistoryEmployeeReportPage } from 'Hooks/useHistoryEmployeeReportPage';
import ReportsLayout from 'Pages/reports/components/ReportsLayout/ReportsLayout';
import ReportsTitle from 'Pages/reports/components/ReportsTitle/ReportsTitle';
import { formatDate } from 'Utils/format-date';
import { HistoryEmployeeReportFilter } from './components/HistoryEmployeeReportFilter/HistoryEmployeeReportFilter';
import ServerPaginatedTable from './components/ServerPaginatedTable/ServerPaginatedTable';
import TotalEmployeeHistoryDetails from './components/TotalEmployeeHistoryDetails/TotalEmployeeHistoryDetails';
import { FaPeopleGroup } from 'react-icons/fa6';
import { MdSchool } from 'react-icons/md';

interface Params {
  employeeId: string;
}

interface RowData {
  id: string;
  courseId: string;
  name: string;
  priorityId: string;
  priorityName: string;
  categoryId: string;
  categoryName: string;
  statusId: string;
  statusName: string;
  assignedDate: string;
  finishedAt: string | React.ReactNode;
  certificationId: string | null;
  downloadCertificate: React.ReactNode;
  [key: string]: unknown;
}

export default function Page() {
  const { employeeId } = useParams<Params>();

  const {
    t2,
    data,
    meta,
    totals,
    loading,
    error,
    employee,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleExport,
    handleDownloadCertificate,
    handleRowClick,
  } = useHistoryEmployeeReportPage(employeeId);

  const rows: RowData[] = data.map(course => ({
    id: course.id,
    courseId: course.courseId,
    name: course.name,
    priorityId: course.priority?.id || '',
    priorityName: course.priority?.name || '-',
    categoryId: course.category?.id || '',
    categoryName: course.category?.name || '-',
    statusId: course.status?.id || '',
    statusName: course.status?.name || '-',
    assignedDate: course.createdAt ? formatDate(course.createdAt) : '-',
    finishedAt: course.finishedAt ? formatDate(course.finishedAt) : <GenericTableText text={t2('not_finished')} />,
    certificationId: course.certificationId,
    downloadCertificate: (
      <CertificateDownload
        certificationId={course.certificationId}
        onDownload={() => handleDownloadCertificate(course.id, course.certificationId ?? '')}
      />
    ),
  }));

  const buttons = [
    {
      icon: <FaPeopleGroup size={24} />,
      tooltip: t2('employees_report_tooltip'),
      route: '/reporte/historial/empleados',
    },
    { icon: <MdSchool size={24} />, tooltip: t2('courses_report_tooltip'), route: '/reporte/historial/cursos' },
  ];

  return (
    <SideBarContainer>
      <Padding padding="1rem 1rem 0 1rem">
        <Breadcrumb
          items={[
            { label: t2('history_reports'), path: '/reporte/historial/empleados' },
            { label: t2('employee_detail'), path: `/reporte/historial/cursos` },
          ]}
        />
      </Padding>
      <ReportsLayout
        title={<ReportsTitle title={t2('title')} accent={`"${employee?.fullName || ''}"`} />}
        buttons={buttons}
        onExport={handleExport}
      />
      <Padding padding="1rem">
        <TotalEmployeeHistoryDetails totals={totals} />
        <HistoryEmployeeReportFilter onSearch={handleSearch} />
        <Padding padding="0.5rem 0" />
        <ServerPaginatedTable<RowData>
          columns={[
            { id: 'name', label: t2('course_name') },
            { id: 'priorityName', label: t2('priority') },
            { id: 'categoryName', label: t2('category') },
            { id: 'assignedDate', label: t2('assigned_date') },
            { id: 'finishedAt', label: t2('finished_date') },
            { id: 'statusName', label: t2('status'), align: 'center' },
            { id: 'downloadCertificate', label: t2('download_certificate'), align: 'center' },
          ]}
          rows={rows}
          meta={meta}
          loading={loading}
          error={error}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onRowClick={handleRowClick}
          getRowContent={(row, columnId) => {
            if (columnId === 'statusName') {
              return <CourseStatusChip statusId={row.statusId} status={row.statusName} />;
            }
            return row[columnId] as React.ReactNode;
          }}
          noDataMessage={t2('no_data')}
        />
      </Padding>
    </SideBarContainer>
  );
}
